import { W_NS, attr, descendants, firstKid, kids } from './ns';
import { readFields } from './field-reader';

export const END_SUFFIX = '-end';

const ANCHOR_ID_BASE = 900000;

const TWIPS = 'dxa';

export function flattenForPreview(part: Document): Document {
  const preview = part.cloneNode(true) as Document;
  let anchorId = ANCHOR_ID_BASE;

  for (const { field, sdt, content } of readFields(preview)) {
    anchor(content, field.id, anchorId++);
    anchor(content, field.id + END_SUFFIX, anchorId++, 'end');

    unwrap(sdt, content);
  }

  unwrapRemaining(preview);
  exposeTableIndents(preview);

  return preview;
}

function exposeTableIndents(preview: Document): void {
  for (const indent of descendants(preview.documentElement, 'tblInd')) {
    const width = attr(indent, 'w');
    const type = attr(indent, 'type') ?? TWIPS;

    if (!width || type !== TWIPS) {
      continue;
    }

    indent.setAttributeNS(W_NS, 'w:left', width);
  }
}

function anchor(content: Element, name: string, id: number, at: 'start' | 'end' = 'start'): void {
  const paragraphs = kids(content, 'p');
  const host =
    paragraphs.length > 0 ? (at === 'start' ? paragraphs[0]! : paragraphs.at(-1)!) : content;

  const mark = content.ownerDocument.createElementNS(W_NS, 'w:bookmarkStart');
  mark.setAttributeNS(W_NS, 'w:id', String(id));
  mark.setAttributeNS(W_NS, 'w:name', name);

  if (at === 'start') {
    host.insertBefore(mark, firstChildAfterProps(host));

    return;
  }

  host.appendChild(mark);
}

function firstChildAfterProps(host: Element): Node | null {
  const props = firstKid(host, 'pPr');

  return props ? props.nextSibling : host.firstChild;
}

function unwrapRemaining(preview: Document): void {
  for (const sdt of descendants(preview.documentElement, 'sdt').reverse()) {
    const content = firstKid(sdt, 'sdtContent');

    if (content) {
      unwrap(sdt, content);
    }
  }
}

function unwrap(sdt: Element, content: Element): void {
  const parent = sdt.parentNode;
  if (!parent) {
    return;
  }

  while (content.firstChild) {
    parent.insertBefore(content.firstChild, sdt);
  }

  parent.removeChild(sdt);
}
